/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { LeadBasicInputs } from 'components/leads/schemas/useLeadBasicSchema'
import { Lead } from 'types/entities/Lead'
import { Company } from 'types/entities/Company'
import { CompanyAddress } from 'types/entities/CompanyAddress'
import { CompanyBranchType } from 'types/enums/CompanyBranchType'
import { LeadSource } from 'types/enums/LeadSource'
import {
    COMPANY_ORIGIN_EVENTFORMULAR,
    normalizeCompanyOrigin,
} from 'utils/companies/companyOriginUtils'

export type LeadFormAddressFields = Pick<
    LeadBasicInputs,
    'street_address' | 'city' | 'postal_code' | 'state' | 'country'
>

const resolveCompanyAddress = (
    company?: Company | null,
    addressId?: number | null
): CompanyAddress | undefined => {
    const addresses = company?.addresses ?? []
    if (addresses.length === 0) {
        return undefined
    }

    if (addressId != null) {
        const selectedAddress = addresses.find((address) => address.id === addressId)
        if (selectedAddress) {
            return selectedAddress
        }
    }

    if (company?.default_address_id != null) {
        return addresses.find((address) => address.id === company.default_address_id) ?? addresses[0]
    }

    return addresses[0]
}

/** Resolve lead form address fields from company addresses (preferred) or deprecated scalar columns. */
export const getLeadFormAddressFields = (
    company?: Company | null,
    addressId?: number | null
): LeadFormAddressFields => {
    if (!company) {
        return {
            street_address: '',
            city: '',
            postal_code: '',
            state: '',
            country: 'Switzerland',
        }
    }

    const address = resolveCompanyAddress(company, addressId)

    if (address) {
        return {
            street_address: address.street_name || '',
            city: address.town || '',
            postal_code: address.postal_code || '',
            state: address.region || '',
            country: address.country || 'Switzerland',
        }
    }

    return {
        street_address: company.street_address || '',
        city: company.city || '',
        postal_code: company.postal_code || '',
        state: company.state || '',
        country: company.country || 'Switzerland',
    }
}

export const SCHOOL_BUSINESS_CATEGORY_ID = 'SchulenBildungAndSoziales'
export const DEFAULT_SCHOOL_CONTACT_POSITION = 'Lehrperson/Schulleitung'
export const EVENT_FORM_CONTACT_ORIGIN = COMPANY_ORIGIN_EVENTFORMULAR

export const isEventFormLead = (lead?: Lead | null): boolean => lead?.source === LeadSource.EventForm

export const isSchoolTrophyLead = (lead?: Lead | null): boolean => {
    if (!lead) {
        return false
    }

    const searchableText = [
        lead.name,
        ...(lead.tags ?? []),
        ...(lead.notes?.map((note) => note.content) ?? []),
    ]
        .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        .join(' ')
        .toLowerCase()

    return (
        searchableText.includes('school-trophy') ||
        searchableText.includes('school_trophy') ||
        searchableText.includes('school trophy')
    )
}

export const resolveSchoolTrophyContactPosition = (
    lead: Lead | null | undefined,
    contactPosition?: string | null
): string => {
    if (isSchoolTrophyLead(lead)) {
        return DEFAULT_SCHOOL_CONTACT_POSITION
    }

    return (contactPosition ?? '').trim()
}

export const resolveEventFormContactOrigin = (
    lead: Lead | null | undefined,
    currentOrigin?: string | null
): string => {
    const rawOrigin = (currentOrigin ?? lead?.contact?.origin ?? '').trim()
    const normalizedOrigin = normalizeCompanyOrigin(rawOrigin)

    if (isEventFormLead(lead) && !normalizedOrigin) {
        return EVENT_FORM_CONTACT_ORIGIN
    }

    return normalizedOrigin
}

const getLeadCanton = (lead?: Lead | null): string => {
    if (!lead) {
        return ''
    }

    return getLeadFormAddressFields(lead.company, lead.company?.default_address_id).state || lead.state || ''
}

export const applyLeadEventFormDefaults = (
    lead: Lead | null | undefined,
    values: Partial<LeadBasicInputs>
): Partial<LeadBasicInputs> => {
    if (!lead) {
        return values
    }

    const next: Partial<LeadBasicInputs> = {...values}

    next.contact_origin = resolveEventFormContactOrigin(lead, next.contact_origin)
    next.company_origin = normalizeCompanyOrigin(next.company_origin ?? lead.company?.origin)

    if (!next.gender) {
        next.gender = lead.contact?.gender || lead.gender || undefined
    }

    if (!next.state?.trim()) {
        next.state = getLeadCanton(lead)
    }

    if (isSchoolTrophyLead(lead)) {
        const currentType = next.company_type as CompanyBranchType | undefined
        if (!currentType || currentType === CompanyBranchType.Company || currentType === CompanyBranchType.Private) {
            next.company_type = CompanyBranchType.School
        }

        if (!next.business_category?.trim()) {
            next.business_category = SCHOOL_BUSINESS_CATEGORY_ID
        }

        next.position = resolveSchoolTrophyContactPosition(lead, next.position)
    }

    return next
}
